import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import gql from "graphql-tag";
import * as React from "react";
import {useState} from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {OnePaneDialog} from "@khanacademy/wonder-blocks-modal";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {
    Body,
    LabelMedium,
    LabelSmall,
} from "@khanacademy/wonder-blocks-typography";
import {useMutation} from "@repo/data-gql";
import {gqlOp, type GqlOperation} from "@repo/data-gql-core";
import {doNotTranslate} from "@repo/i18n";

import type {
    disableTotpTwoFactor,
    disableTotpTwoFactorVariables,
} from "./__graphql-types__/disableTotpTwoFactor.ts";

type Props = {
    kaid: string;
    /* A function to call when the user has successfully changed their settings.
    Pops up a toast and refreshes the page. */
    onSettingsChanged: () => unknown;
};

type DisableMutationState =
    | {status: "waiting"}
    | {status: "success"; message: string}
    | {status: "error"; message: string};

export const DISABLE_2FA_MUTATION: GqlOperation<
    disableTotpTwoFactor,
    disableTotpTwoFactorVariables
> = gqlOp(gql`
    mutation disableTotpTwoFactor($kaid: String!) {
        disableTotpTwoFactor(targetKaid: $kaid) {
            error {
                code
            }
        }
    }
`);

// NOTE: This is a WIP modal, behind a feature flag ("two_factor_authentication")
// and is not enabled for any users.
// This is a modal for disabling time-based one time password (TOTP) two-factor
// authentication which is set up through an authenticator app. This modal is
// behind reauth, which will check for a valid 2FA code before showing this
// modal, so this modal can just call the disableTotpTwoFactor mutation.
export function DisableTotpTwoFactorModal({
    kaid,
    onSettingsChanged,
}: Props): React.ReactElement {
    const [disableMutationState, setDisableMutationState] =
        useState<DisableMutationState>({status: "waiting"});
    const [disableTotpTwoFactorMutation, result] =
        useMutation(DISABLE_2FA_MUTATION);

    const isLoading =
        result?.status === "loading" || result?.status === "no-data";

    const onSubmit = async () => {
        const response = await disableTotpTwoFactorMutation({
            kaid: kaid,
        });
        if (response.status === "error") {
            setDisableMutationState({
                status: "error",
                message: t`Oops! Something went wrong. Please try again.`,
            });
        }
        if (response.status === "success") {
            const error = response.data?.disableTotpTwoFactor?.error;
            if (error && error.code === "UNAUTHORIZED") {
                setDisableMutationState({
                    status: "error",
                    message: doNotTranslate`Unauthorized to disable 2FA.`,
                });
            } else if (error) {
                setDisableMutationState({
                    status: "error",
                    message: t`Oops! Something went wrong. Please try again.`,
                });
            } else {
                setDisableMutationState({
                    status: "success",
                    message: doNotTranslate`Two-factor authentication has been disabled.`,
                });
                onSettingsChanged();
            }
        }
    };

    return (
        <OnePaneDialog
            title={doNotTranslate`Disable two-factor authentication`}
            content={
                <View>
                    <LabelMedium>
                        {doNotTranslate`Are you sure you want to disable two-factor authentication? Doing so will remove the recommended security protections on your account.`}
                    </LabelMedium>
                    <Strut size={spacing.small_12} />
                    {disableMutationState.status === "error" && (
                        <Body style={styles.errorText}>
                            {disableMutationState.message}
                        </Body>
                    )}
                    {disableMutationState.status === "success" && (
                        <LabelSmall style={styles.successText}>
                            {disableMutationState.message}
                        </LabelSmall>
                    )}
                </View>
            }
            footer={
                <Button
                    onClick={onSubmit}
                    disabled={
                        !kaid || disableMutationState.status === "success"
                    }
                    spinner={isLoading}
                    actionType="destructive"
                >
                    {doNotTranslate`Disable 2FA`}
                </Button>
            }
            style={styles.modal}
        />
    );
}

const styles = StyleSheet.create({
    errorText: {
        color: color.red,
        marginTop: spacing.xxxSmall_4,
    },
    modal: {
        height: "90%",
        maxHeight: 300,
    },
    successText: {
        color: color.green,
    },
});
